import type { SegmentLandingProps } from "@/components/SegmentLanding";

/**
 * Landing pages por segmento geradas a partir de dados.
 * Cada entrada tem conteúdo próprio (não é template genérico) para ranquear
 * em buscas de intenção comercial e ser citada por IAs.
 */
export const SEGMENTS: SegmentLandingProps[] = [
  {
    segment: "Clínica",
    slug: "agente-ia-clinica",
    metaTitle: "Agente de IA para Clínica: agenda cheia e 70% menos faltas",
    metaDescription:
      "Agente de IA para clínicas e consultórios que agenda consultas, confirma presença e reduz faltas no WhatsApp e Instagram 24h. CRM incluso. Veja como funciona.",
    heroTitle: "Agente de IA para Clínica: agenda cheia, recepção livre",
    heroSubtitle:
      "Marque consultas, confirme presença, faça pré-triagem e reduza faltas — pelo WhatsApp e pelo Instagram, 24 horas por dia, com o CRM da OP2P organizando cada paciente.",
    intro:
      "Recepção de clínica vive entre o telefone, o balcão, o WhatsApp e o Direct do Instagram — e é sempre alguma mensagem que fica para depois. Os agentes de IA da OP2P respondem na hora em qualquer um desses canais, agendam dentro da sua disponibilidade, confirmam 24h antes e devolvem o horário à agenda quando alguém desmarca, com tudo reunido no mesmo CRM. A recepção volta a atender quem está na frente dela. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O que faz um agente de IA para clínica?",
        a: "Ele atende o paciente no WhatsApp e no Instagram, informa convênios e valores, agenda e reagenda consultas na agenda real da clínica, envia lembretes automáticos, faz pré-triagem de sintomas e registra tudo no CRM. Casos clínicos ou sensíveis são transferidos para a equipe humana.",
      },
      {
        q: "Reduz falta de paciente mesmo?",
        a: "Sim. O maior ganho vem do lembrete automático 24h e 2h antes com confirmação em um clique. Clínicas que automatizam confirmação costumam derrubar o no-show de forma expressiva, porque o horário desmarcado volta à agenda e é oferecido à lista de espera.",
      },
      {
        q: "Isso respeita a LGPD e o sigilo do paciente?",
        a: "Sim. Os dados contam com controle de acesso por usuário e histórico auditável. O agente não emite diagnóstico nem prescrição — ele organiza atendimento, agenda e informações administrativas.",
      },
    ],
    benefits: [
      { title: "Agendamento 24h", desc: "O paciente marca no domingo à noite. A agenda amanhece preenchida." },
      { title: "Confirmação automática", desc: "Lembrete 24h e 2h antes, com confirmação em um toque. Falta despenca." },
      { title: "Pré-triagem", desc: "Coleta motivo da consulta, convênio e histórico antes do atendimento." },
      { title: "Lista de espera", desc: "Desmarcou? O horário é oferecido na hora a quem está esperando." },
      { title: "Convênios e valores", desc: "Responde na hora as perguntas que mais consomem a recepção." },
      { title: "CRM por paciente", desc: "Cada conversa — venha do WhatsApp ou do Instagram — fica no histórico do paciente, movido por IA." },
    ],
    useCases: [
      "Paciente novo pergunta se atende o convênio dele — o agente responde e já agenda.",
      "Confirmação automática de todas as consultas do dia seguinte.",
      "Paciente cancela às 22h — o agente oferece o horário a outro paciente na fila.",
      "Retorno de 6 meses: o agente aciona sozinho quem está na hora de voltar.",
      "Orçamento de procedimento estético enviado e acompanhado com follow-up.",
    ],
    faq: [
      { q: "Integra com a agenda que já uso?", a: "Sim. Integramos com as principais agendas e sistemas de gestão clínica, ou você pode usar a agenda nativa do OP2P." },
      { q: "O agente dá orientação médica?", a: "Não. Ele faz triagem administrativa e encaminhamento. Qualquer conteúdo clínico é direcionado ao profissional." },
      { q: "Serve para consultório com um único profissional?", a: "Sim. O plano inicial já cobre um consultório, e o CRM cresce junto com a clínica." },
      { q: "Quanto tempo leva para entrar no ar?", a: "A implantação assistida costuma ser concluída em poucos dias, com nossa equipe configurando fluxos, agenda e integrações." },
    ],
    related: [{ label: "Blog: erros no atendimento por WhatsApp", to: "/blog/5-erros-fatais-atendimento-whatsapp" }],
  },
  {
    segment: "Odontologia",
    slug: "agente-ia-odontologia",
    metaTitle: "Agente de IA para Odontologia: mais avaliações e orçamentos fechados",
    metaDescription:
      "Agente de IA para dentistas que agenda avaliações, envia orçamentos, faz follow-up de tratamento e reduz faltas pelo WhatsApp e Instagram. CRM incluso.",
    heroTitle: "Agente de IA para Odontologia: mais avaliações, menos orçamento parado",
    heroSubtitle:
      "Agende avaliações, envie orçamentos, faça follow-up dos tratamentos não fechados e reduza faltas — automaticamente, no WhatsApp e no Instagram.",
    intro:
      "Em odontologia o dinheiro está no orçamento que ninguém acompanhou. Os agentes de IA da OP2P agendam a avaliação, enviam o orçamento e continuam a conversa nos dias seguintes — seja ela iniciada no WhatsApp ou no Direct do Instagram —, enquanto o CRM mostra exatamente quantos tratamentos estão parados e em que etapa. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como o agente de IA aumenta o faturamento de uma clínica odontológica?",
        a: "Ele ataca as três maiores perdas: demora para responder o primeiro contato, falta na avaliação e orçamento sem follow-up. O agente responde em segundos, confirma a presença e reaquece automaticamente cada orçamento aberto.",
      },
      {
        q: "Consigo acompanhar os orçamentos?",
        a: "Sim. Cada paciente vira um card no Kanban do CRM movido por IA: avaliação agendada, orçamento enviado, em negociação, tratamento fechado. Você vê o valor total parado em cada etapa.",
      },
    ],
    benefits: [
      { title: "Avaliação agendada na hora", desc: "Do primeiro 'oi', no WhatsApp ou no Instagram, até o horário marcado sem passar pela recepção." },
      { title: "Orçamento com follow-up", desc: "O agente retoma o contato de quem não respondeu — na cadência certa." },
      { title: "Lembrete de sessão", desc: "Tratamentos longos exigem presença. O agente lembra a cada etapa." },
      { title: "Planos e parcelamento", desc: "Responde dúvidas de convênio, formas de pagamento e parcelas." },
      { title: "Reativação de pacientes", desc: "Quem não volta há um ano recebe convite automático para limpeza." },
      { title: "Kanban por tratamento", desc: "Veja o valor em negociação e o que precisa de atenção hoje." },
    ],
    useCases: [
      "Paciente comenta em uma foto de sorriso no Instagram perguntando o preço do implante — o agente responde e agenda avaliação.",
      "Orçamento de R$ 8.000 enviado há 5 dias sem resposta: o agente reaquece sozinho.",
      "Confirmação automática de cada sessão de aparelho ou canal.",
      "Campanha de limpeza semestral para toda a base, direto do CRM.",
    ],
    faq: [
      { q: "Integra com Dental Office, Simples Dental e similares?", a: "Sim, integramos com os principais sistemas odontológicos, além da agenda nativa do OP2P." },
      { q: "O agente negocia valores?", a: "Ele apresenta condições configuradas por você e transfere para a equipe quando a negociação exige flexibilidade." },
      { q: "Funciona para clínica com vários dentistas?", a: "Sim. Cada profissional tem agenda e fila próprias, com um agente por departamento." },
    ],
  },
  {
    segment: "Academia",
    slug: "agente-ia-academia",
    metaTitle: "Agente de IA para Academia: matrículas 24h e menos cancelamento",
    metaDescription:
      "Agente de IA para academias e estúdios que responde planos, agenda aula experimental, cobra mensalidade e recupera alunos inativos no WhatsApp e Instagram.",
    heroTitle: "Agente de IA para Academia: matrícula no WhatsApp, aluno que fica",
    heroSubtitle:
      "Responda planos, agende aula experimental, acompanhe matrícula e recupere aluno sumido — no WhatsApp e no Instagram, automaticamente, com CRM acompanhando cada lead.",
    intro:
      "Academia perde aluno em dois momentos: no primeiro contato que demorou a ser respondido e no mês em que ele parou de aparecer. Os agentes de IA da OP2P respondem na hora — seja no WhatsApp ou nos comentários e Direct do Instagram —, agendam a experimental e monitoram quem sumiu para trazer de volta antes do cancelamento. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como o agente de IA ajuda a academia a vender mais planos?",
        a: "Ele responde em segundos as perguntas de preço e horário, agenda a aula experimental, lembra o aluno de comparecer e faz follow-up de quem visitou e não fechou — tudo registrado no CRM.",
      },
      {
        q: "Dá para reduzir cancelamento?",
        a: "Sim. O agente identifica queda de frequência e aciona o aluno com uma mensagem personalizada antes que ele decida cancelar, além de conduzir a renovação automática do plano.",
      },
    ],
    benefits: [
      { title: "Resposta em segundos", desc: "Preço, horário e modalidade respondidos na hora, no WhatsApp ou no Instagram, dia e noite." },
      { title: "Aula experimental", desc: "Agenda, confirma e lembra — o visitante realmente aparece." },
      { title: "Recuperação de inativo", desc: "Aluno sumiu? O agente reativa antes de virar cancelamento." },
      { title: "Cobrança amigável", desc: "Lembretes de mensalidade sem constranger o aluno." },
      { title: "Venda de personal e extras", desc: "Oferta certa, para o aluno certo, no momento certo." },
      { title: "Funil de matrícula", desc: "CRM mostra visita, experimental e matrícula em Kanban." },
    ],
    useCases: [
      "Lead comenta no anúncio do Instagram perguntando o valor do plano anual — agente responde e agenda visita.",
      "Aluno não treina há 10 dias — agente manda mensagem de retomada.",
      "Campanha de renovação enviada para toda a base do mês.",
      "Aula experimental confirmada automaticamente na véspera.",
    ],
    faq: [
      { q: "Integra com sistema de catraca ou gestão?", a: "Sim, integramos com os principais sistemas de gestão de academias e estúdios." },
      { q: "Serve para estúdio de pilates ou crossfit?", a: "Sim. O fluxo é adaptado às modalidades, turmas e limite de vagas por horário." },
      { q: "O agente cobra inadimplente?", a: "Envia lembretes configuráveis e educados, com opção de transferir casos sensíveis para a equipe." },
    ],
  },
  {
    segment: "Pet Shop",
    slug: "agente-ia-petshop",
    metaTitle: "Agente de IA para Pet Shop: banho e tosa agendados no WhatsApp",
    metaDescription:
      "Agente de IA para pet shop e clínica veterinária: agenda banho e tosa, lembra vacina, recebe pedidos de ração e fideliza tutores no WhatsApp e Instagram 24h.",
    heroTitle: "Agente de IA para Pet Shop: agenda cheia e tutor fiel",
    heroSubtitle:
      "Banho e tosa agendados sozinhos, lembrete de vacina, pedido de ração recorrente e atendimento veterinário organizado — tudo no WhatsApp e no Instagram.",
    intro:
      "Pet shop vive de recorrência: banho a cada 15 dias, ração todo mês, vacina todo ano. Os agentes de IA da OP2P transformam essa recorrência em receita previsível — eles lembram, agendam e registram cada pet no CRM com histórico completo, não importa se a conversa começou no WhatsApp ou no Instagram. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O que o agente de IA faz num pet shop?",
        a: "Agenda banho e tosa dentro da capacidade real do dia, envia lembrete de vacina e vermífugo, recebe pedido de ração recorrente, responde preços por porte e raça e registra o histórico de cada pet no CRM.",
      },
      {
        q: "Funciona também para clínica veterinária?",
        a: "Sim. Consultas, retornos e emergências entram no mesmo fluxo, com transferência imediata para o veterinário nos casos urgentes.",
      },
    ],
    benefits: [
      { title: "Banho e tosa 24h", desc: "Agenda por porte, raça e serviço, respeitando a capacidade do dia." },
      { title: "Lembrete de vacina", desc: "Ninguém esquece a data — e o pet volta à loja." },
      { title: "Ração recorrente", desc: "O agente pergunta na hora certa: 'a ração do Thor está acabando?'" },
      { title: "Histórico do pet", desc: "Cada pet com ficha, serviços e preferências no CRM, venha o contato do WhatsApp ou do Instagram." },
      { title: "Táxi dog e delivery", desc: "Agenda busca e entrega sem ocupar o balcão." },
      { title: "Fidelidade automática", desc: "A cada X banhos, um benefício — controlado pela IA." },
    ],
    useCases: [
      "Tutor pede banho pelo Direct do Instagram para sábado — agente confirma horário e porte.",
      "Vacina antirrábica vence em 15 dias — agente avisa e agenda.",
      "Recompra de ração sugerida no mês exato do consumo.",
      "Emergência veterinária transferida na hora para o plantonista.",
    ],
    faq: [
      { q: "Consigo limitar a quantidade de banhos por dia?", a: "Sim. A agenda respeita a capacidade por horário, porte e profissional." },
      { q: "O agente conhece meus preços?", a: "Sim. Tabela por porte, raça e serviço fica configurada e é respondida na hora." },
      { q: "Tem CRM incluso?", a: "Sim. Todos os planos incluem o CRM inteligente com Kanban movido por IA." },
    ],
  },
  {
    segment: "Escola",
    slug: "agente-ia-escola-curso",
    metaTitle: "Agente de IA para Escola e Curso: matrículas sem perder lead",
    metaDescription:
      "Agente de IA para escolas e cursos que responde dúvidas de matrícula, envia valores, agenda visitas e faz follow-up dos pais no WhatsApp e Instagram 24h.",
    heroTitle: "Agente de IA para Escola e Curso: matrícula que não esfria",
    heroSubtitle:
      "Responda pais e alunos na hora — no WhatsApp e no Instagram —, envie valores e documentos, agende visitas e acompanhe cada matrícula em um CRM que se move sozinho.",
    intro:
      "Período de matrícula é uma corrida contra o tempo: quem responde primeiro leva o aluno. Os agentes de IA da OP2P atendem cada família em segundos, seja pelo WhatsApp ou pelos comentários e Direct do Instagram, enviam a lista de documentos, agendam a visita e mantêm o follow-up até a matrícula assinada. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como o agente de IA ajuda na captação de alunos?",
        a: "Ele responde imediatamente às perguntas de valores, turnos, vagas e documentação, agenda visitas e mantém uma cadência de follow-up com as famílias indecisas — o que geralmente decide a matrícula.",
      },
      {
        q: "Serve para curso livre, idiomas e pré-vestibular?",
        a: "Sim. Turmas, níveis, horários e testes de nivelamento entram no fluxo, com matrícula e pagamento encaminhados automaticamente.",
      },
    ],
    benefits: [
      { title: "Atendimento imediato", desc: "A família recebe resposta no WhatsApp ou no Instagram enquanto ainda está pesquisando escolas." },
      { title: "Documentos e valores", desc: "Lista, tabela e condições enviadas sem depender da secretaria." },
      { title: "Visita agendada", desc: "Agenda, confirma e lembra a visita à unidade." },
      { title: "Follow-up de matrícula", desc: "Cadência automática até a decisão — nada esfria no meio." },
      { title: "Comunicados", desc: "Avisos, calendário e recados enviados em massa pelo WhatsApp." },
      { title: "Funil por turma", desc: "CRM mostra vagas, interessados e matrículas por série ou turma." },
    ],
    useCases: [
      "Mãe comenta no anúncio de matrículas do Instagram perguntando o valor do integral — agente responde e agenda visita.",
      "Rematrícula da base atual disparada com um clique.",
      "Teste de nivelamento agendado automaticamente para curso de idiomas.",
      "Aviso de reunião de pais enviado a todas as turmas.",
    ],
    faq: [
      { q: "Integra com sistema acadêmico?", a: "Sim, integramos com os principais sistemas de gestão escolar." },
      { q: "Posso ter um agente para secretaria e outro para captação?", a: "Sim. O sistema é multiagentes — cada departamento tem o seu." },
      { q: "Tem controle de quem falou com quem?", a: "Sim. Todo o histórico fica no CRM, com registro por atendente e por agente." },
    ],
  },
  {
    segment: "Oficina",
    slug: "agente-ia-oficina-mecanica",
    metaTitle: "Agente de IA para Oficina Mecânica: orçamentos e agenda no WhatsApp",
    metaDescription:
      "Agente de IA para oficina mecânica e autocenter: agenda serviços, envia orçamento, avisa revisão e faz follow-up de cliente no WhatsApp e Instagram 24h.",
    heroTitle: "Agente de IA para Oficina: orçamento respondido, box sempre cheio",
    heroSubtitle:
      "Agende serviços, envie orçamentos, avise sobre revisão e traga o cliente de volta — no WhatsApp e no Instagram, sem parar o que você está fazendo para olhar o celular.",
    intro:
      "Na oficina, quem está com as mãos no motor não responde WhatsApp nem Instagram — e o cliente vai para a próxima. Os agentes de IA da OP2P atendem em qualquer um dos dois canais, entendem o problema descrito, agendam o box e enviam o orçamento; o CRM acompanha cada veículo até a aprovação. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O agente de IA entende o problema que o cliente descreve?",
        a: "Sim. Ele interpreta a descrição em linguagem natural ('está fazendo barulho ao frear'), classifica o tipo de serviço, agenda o box adequado e já prepara a informação para o mecânico.",
      },
      {
        q: "Como fica o acompanhamento do orçamento?",
        a: "Cada veículo vira um card no CRM: diagnóstico, orçamento enviado, aprovado, em execução, entregue. Orçamentos parados recebem follow-up automático.",
      },
    ],
    benefits: [
      { title: "Agenda de box", desc: "Serviços marcados por tipo e duração, sem furo na agenda." },
      { title: "Orçamento com follow-up", desc: "Enviou e o cliente sumiu? O agente retoma sozinho." },
      { title: "Aviso de revisão", desc: "Por quilometragem ou tempo — o cliente volta na hora certa." },
      { title: "Status do serviço", desc: "Cliente pergunta 'já ficou pronto?' pelo WhatsApp ou Instagram e recebe a resposta na hora." },
      { title: "Peças e prazos", desc: "Informa disponibilidade e prazos configurados por você." },
      { title: "Histórico por veículo", desc: "Placa, serviços anteriores e recomendações no CRM." },
    ],
    useCases: [
      "Cliente descreve barulho na suspensão pelo Direct do Instagram — agente agenda avaliação.",
      "Orçamento de R$ 2.400 enviado ontem: agente faz follow-up hoje.",
      "Revisão de 10.000 km avisada automaticamente pela IA.",
      "Cliente acompanha status do carro sem ligar para a oficina.",
    ],
    faq: [
      { q: "Serve para autocenter e centro automotivo?", a: "Sim, incluindo pneus, alinhamento, funilaria e serviços rápidos." },
      { q: "Integra com sistema de ordem de serviço?", a: "Sim, integramos com os principais sistemas do setor." },
      { q: "Preciso de equipe técnica para usar?", a: "Não. A implantação é assistida pela nossa equipe, sem código." },
    ],
  },
  {
    segment: "E-commerce",
    slug: "agente-ia-ecommerce",
    metaTitle: "Agente de IA para E-commerce: recupere carrinho e venda no WhatsApp",
    metaDescription:
      "Agente de IA para e-commerce que recupera carrinho abandonado, responde rastreio e troca, faz pós-venda e vende pelo WhatsApp e Instagram com CRM integrado.",
    heroTitle: "Agente de IA para E-commerce: carrinho recuperado, SAC no automático",
    heroSubtitle:
      "Recupere carrinho abandonado, responda rastreio e troca em segundos e transforme o WhatsApp e o Instagram no seu canal de vendas mais rentável.",
    intro:
      "No e-commerce, o WhatsApp e o Instagram fecham o que o site não fechou. Os agentes de IA da OP2P recuperam carrinho abandonado com conversa de verdade, respondem comentários e Direct de quem viu o produto no anúncio, resolvem as dúvidas de rastreio e troca que travam o SAC e devolvem à equipe apenas o que realmente precisa de gente. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como o agente de IA recupera carrinho abandonado?",
        a: "Ele aborda o cliente no WhatsApp pouco depois do abandono, tira a dúvida que travou a compra (frete, prazo, pagamento), oferece a condição configurada e devolve o link de checkout — com todo o contato registrado no CRM.",
      },
      {
        q: "O agente resolve rastreio e troca sozinho?",
        a: "Sim. As perguntas de status de pedido, prazo, troca e devolução são as mais repetitivas do SAC e o agente resolve na hora, 24 horas por dia, escalando apenas as exceções.",
      },
    ],
    benefits: [
      { title: "Carrinho recuperado", desc: "Abordagem no WhatsApp minutos depois do abandono." },
      { title: "SAC 24h", desc: "Rastreio, prazo, troca e devolução respondidos na hora." },
      { title: "Venda assistida", desc: "O agente recomenda produto — inclusive a quem comentou no anúncio do Instagram — e envia o link de pagamento." },
      { title: "Pós-venda ativo", desc: "Avaliação, recompra e cupom no momento certo." },
      { title: "Campanhas segmentadas", desc: "Disparos por comportamento direto do CRM." },
      { title: "Integração com a loja", desc: "Pedidos e status sincronizados com o sistema." },
    ],
    useCases: [
      "Carrinho de R$ 450 abandonado às 21h — agente recupera às 21h15.",
      "'Cadê meu pedido?' perguntado no Direct do Instagram e respondido em 5 segundos, sem fila.",
      "Cliente que comprou há 60 dias recebe oferta de recompra.",
      "Troca solicitada e encaminhada com etiqueta, sem atendente.",
    ],
    faq: [
      { q: "Integra com Shopify, Nuvemshop, VTEX?", a: "Sim, integramos com as principais soluções de e-commerce do mercado." },
      { q: "O agente processa pagamento?", a: "Ele envia o link de checkout ou pagamento gerado pelo seu sistema de vendas." },
      { q: "Consigo medir a receita recuperada?", a: "Sim. O CRM mostra os valores por etapa do funil e o que foi fechado via agente." },
    ],
  },
  {
    segment: "Contabilidade",
    slug: "agente-ia-contabilidade",
    metaTitle: "Agente de IA para Contabilidade: prazos e documentos sem correria",
    metaDescription:
      "Agente de IA para escritório contábil que cobra documentos, responde dúvidas de rotina, lembra prazos e organiza clientes no WhatsApp e Instagram com CRM.",
    heroTitle: "Agente de IA para Contabilidade: documento na mão, prazo em dia",
    heroSubtitle:
      "Cobre documentos, responda dúvidas repetitivas, lembre prazos fiscais e libere sua equipe para o que exige um contador de verdade.",
    intro:
      "Escritório contábil gasta metade do mês cobrando documento e respondendo a mesma pergunta. Os agentes de IA da OP2P fazem essa cobrança sozinhos, com educação e insistência na medida, atendem também quem chega pelo Instagram do escritório e mantêm no CRM o status de cada cliente e cada obrigação. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O que o agente de IA resolve num escritório contábil?",
        a: "Cobrança recorrente de documentos, dúvidas de rotina (guia, boleto, folha, pró-labore), lembretes de prazos fiscais e triagem de novos clientes. O que exige análise técnica vai direto para o contador responsável.",
      },
      {
        q: "É seguro para dados fiscais?",
        a: "Sim. Acesso controlado por usuário, histórico auditável e conformidade com a LGPD.",
      },
    ],
    benefits: [
      { title: "Cobrança de documentos", desc: "Insistência automática e educada até o arquivo chegar." },
      { title: "Dúvidas de rotina", desc: "Guias, boletos e prazos respondidos sem ocupar a equipe." },
      { title: "Calendário fiscal", desc: "Avisos automáticos de obrigações por cliente." },
      { title: "Onboarding de cliente", desc: "Coleta de dados e documentos de abertura sem retrabalho, venha o contato do WhatsApp ou do Instagram." },
      { title: "Departamentos separados", desc: "Um agente para fiscal, outro para folha, outro para comercial." },
      { title: "Status por cliente", desc: "CRM mostra quem entregou, quem falta e o que está vencendo." },
    ],
    useCases: [
      "Dia 5: agente cobra as notas de todos os clientes que ainda não enviaram.",
      "Cliente pede a guia do DAS — agente envia na hora.",
      "Novo cliente chega pelo Instagram perguntando sobre abertura de empresa e o agente conduz todo o onboarding documental.",
      "Prazo do IRPJ chegando: aviso automático para a carteira inteira.",
    ],
    faq: [
      { q: "Substitui o contador?", a: "Não. Ele elimina a parte repetitiva para que o contador cuide da análise e do relacionamento." },
      { q: "Integra com sistema contábil?", a: "Sim, integramos com os principais sistemas e com o CRM nativo da OP2P." },
      { q: "Dá para separar por departamento?", a: "Sim. O sistema é multiagentes: fiscal, pessoal, societário e comercial." },
    ],
    related: [{ label: "Blog: agente de IA para contabilidade", to: "/blog/agente-ia-contabilidade-escritorio" }],
  },
  {
    segment: "Advocacia",
    slug: "agente-ia-advocacia",
    metaTitle: "Agente de IA para Advocacia: triagem de casos 24h com ética da OAB",
    metaDescription:
      "Agente de IA para escritório de advocacia que faz triagem de casos, agenda consultas e informa andamento no WhatsApp e Instagram, com CRM e respeito ao Código de Ética.",
    heroTitle: "Agente de IA para Advocacia: triagem 24h, escritório organizado",
    heroSubtitle:
      "Faça a triagem de novos casos, agende consultas e responda andamento processual no WhatsApp e no Instagram sem violar o Código de Ética — com todo o histórico no CRM.",
    intro:
      "Escritório de advocacia perde caso bom por demora e perde tempo com caso que não é da área. Os agentes de IA da OP2P fazem a triagem inicial, identificam a área, agendam a consulta com o advogado certo e mantêm o cliente informado — seja o contato feito por WhatsApp ou pelo Direct do Instagram, sempre dentro dos limites de publicidade da OAB. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O agente de IA pode ser usado por advogado?",
        a: "Sim, como ferramenta de atendimento e organização. Ele faz triagem, agenda e informa andamento; não dá parecer jurídico, não capta clientela de forma ostensiva e respeita as regras de publicidade do Código de Ética da OAB.",
      },
      {
        q: "Como funciona a triagem de casos?",
        a: "O agente coleta os fatos essenciais, identifica a área do direito, descarta o que o escritório não atende e agenda consulta com o advogado responsável — com tudo registrado no CRM.",
      },
    ],
    benefits: [
      { title: "Triagem por área", desc: "Trabalhista, cível, família, previdenciário — cada caso ao advogado certo." },
      { title: "Atendimento 24h", desc: "Quem procura advogado à noite, no WhatsApp ou no Instagram, não espera até segunda-feira." },
      { title: "Consulta agendada", desc: "Agenda, confirma e lembra a reunião com o cliente." },
      { title: "Andamento processual", desc: "Responde status sem ocupar o advogado a cada ligação." },
      { title: "Documentos do caso", desc: "Coleta e organiza a documentação inicial automaticamente." },
      { title: "Ética preservada", desc: "Comunicação informativa, sem mercantilização da profissão." },
    ],
    useCases: [
      "Consulta trabalhista chega pelo Direct do Instagram às 23h — agente faz triagem e agenda para terça.",
      "Cliente pergunta andamento — agente responde o status configurado.",
      "Documentos iniciais coletados antes da primeira reunião.",
      "Caso fora da área do escritório identificado e encerrado com cordialidade.",
    ],
    faq: [
      { q: "O agente dá orientação jurídica?", a: "Não. Ele coleta informações e encaminha. A orientação é sempre do advogado." },
      { q: "Isso fere a publicidade da OAB?", a: "Não, quando usado como canal de atendimento a quem já procurou o escritório, com linguagem informativa — que é como configuramos." },
      { q: "Serve para escritório pequeno?", a: "Sim. Um único advogado já ganha horas por semana com a triagem automática." },
    ],
    related: [{ label: "Blog: agente de IA para advocacia", to: "/blog/agente-ia-advocacia-escritorio-juridico" }],
  },
  {
    segment: "Corretora de Seguros",
    slug: "agente-ia-seguros",
    metaTitle: "Agente de IA para Seguros: cotação rápida e renovação em dia",
    metaDescription:
      "Agente de IA para corretora de seguros: coleta dados de cotação, acompanha propostas, lembra renovação e organiza a carteira no WhatsApp e Instagram com CRM.",
    heroTitle: "Agente de IA para Seguros: cotação rápida, renovação que não escapa",
    heroSubtitle:
      "Colete dados de cotação, acompanhe propostas, lembre renovações e mantenha a carteira inteira organizada — com IA trabalhando no WhatsApp e no Instagram.",
    intro:
      "Corretor perde renovação por esquecimento e perde cotação por demora. Os agentes de IA da OP2P coletam os dados do segurado, seja o contato vindo do WhatsApp ou de um comentário no anúncio do Instagram, acompanham a proposta com follow-up e avisam cada vencimento com antecedência — a carteira inteira dentro do CRM. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como o agente de IA ajuda o corretor de seguros?",
        a: "Ele coleta os dados necessários para cotar (veículo, CEP, perfil de condutor, uso), acompanha a proposta enviada com follow-up automático e dispara lembretes de renovação com semanas de antecedência.",
      },
      {
        q: "Perco o relacionamento com o cliente?",
        a: "Não. O agente cuida da coleta e do lembrete; a negociação e a consultoria continuam com você, agora com histórico completo de cada segurado no CRM.",
      },
    ],
    benefits: [
      { title: "Coleta de cotação", desc: "Dados completos do segurado sem ida e volta de mensagens, no WhatsApp ou no Instagram." },
      { title: "Follow-up de proposta", desc: "Proposta enviada é proposta acompanhada, na cadência certa." },
      { title: "Alerta de renovação", desc: "Aviso 60, 30 e 7 dias antes do vencimento da apólice." },
      { title: "Aviso de sinistro", desc: "Orienta o primeiro passo e aciona o corretor na hora." },
      { title: "Cross-sell", desc: "Auto, residencial, vida e empresarial ofertados no momento certo." },
      { title: "Carteira no CRM", desc: "Vencimentos, prêmios e status por segurado em Kanban." },
    ],
    useCases: [
      "Lead comenta no anúncio de seguro auto no Instagram pedindo cotação — agente coleta tudo e entrega pronta para você.",
      "Apólice vence em 30 dias: agente inicia a renovação sozinho.",
      "Cliente comunica sinistro à meia-noite e recebe orientação imediata.",
      "Campanha de seguro residencial para toda a carteira de auto.",
    ],
    faq: [
      { q: "Integra com multicálculo?", a: "Sim, integramos com os principais sistemas usados por corretoras." },
      { q: "O agente emite apólice?", a: "Não. Ele organiza, coleta e acompanha; a emissão continua no seu processo." },
      { q: "Serve para corretor autônomo?", a: "Sim. O ganho de tempo em renovação já compensa para quem trabalha sozinho." },
    ],
    related: [{ label: "Blog: agente de IA para corretora de seguros", to: "/blog/agente-ia-seguros-corretora" }],
  },
  {
    segment: "Corretora de Planos de Saúde",
    slug: "agente-ia-plano-de-saude",
    metaTitle: "Agente de IA para Plano de Saúde: cotação na hora, venda no WhatsApp",
    metaDescription:
      "Agente de IA para corretores de plano de saúde: coleta idades, cidade e CNPJ, organiza cotações PF, PME e adesão e faz follow-up no WhatsApp e Instagram com CRM.",
    heroTitle: "Agente de IA para Plano de Saúde: cotação rápida, lead que não esfria",
    heroSubtitle:
      "Responda na hora quem pede cotação de plano de saúde, colete os dados certos para PF, PME e adesão e acompanhe cada proposta até a assinatura — no WhatsApp e no Instagram.",
    intro:
      "Quem procura plano de saúde pede cotação para várias corretoras ao mesmo tempo. Ganha quem responde primeiro, com as perguntas certas. Os agentes de IA da OP2P atendem o lead do anúncio no mesmo minuto, perguntam idades das vidas, cidade, se existe CNPJ ou entidade de classe e preferência de rede, e entregam a cotação pronta para o corretor — tudo registrado no CRM. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O que faz um agente de IA para corretor de plano de saúde?",
        a: "Ele atende o lead no WhatsApp e no Instagram, coleta idades, cidade, modalidade (individual, familiar, PME ou adesão) e preferências de rede e hospitais, qualifica o interesse, agenda a conversa com o corretor e faz follow-up da proposta enviada. Tudo fica no histórico do CRM.",
      },
      {
        q: "O agente informa preço de plano de saúde?",
        a: "Ele pode apresentar faixas e tabelas que a corretora cadastrar, mas a proposta final continua com o corretor, que confere carências, rede credenciada e regras de cada operadora.",
      },
      {
        q: "Serve para planos PME com CNPJ?",
        a: "Sim. O agente identifica se o lead tem CNPJ ativo, quantas vidas quer incluir e quem são os dependentes, o que acelera a cotação empresarial.",
      },
    ],
    benefits: [
      { title: "Resposta em segundos", desc: "O lead do anúncio é atendido antes de falar com a próxima corretora." },
      { title: "Coleta de vidas e idades", desc: "Idades, dependentes, cidade e modalidade sem ida e volta de mensagens." },
      { title: "PF, PME e adesão", desc: "Roteiros diferentes para cada modalidade e para cada entidade de classe." },
      { title: "Follow-up de proposta", desc: "Proposta enviada é proposta acompanhada até a assinatura." },
      { title: "Documentação organizada", desc: "Lembra e confere os documentos pendentes de cada beneficiário." },
      { title: "Carteira no CRM", desc: "Aniversários de contrato, reajustes e oportunidades de upgrade no Kanban." },
    ],
    useCases: [
      "Lead clica no anúncio de plano de saúde às 23h e recebe a primeira pergunta na hora.",
      "Empresa com 8 funcionários pede cotação PME: o agente coleta CNPJ, vidas e idades e entrega ao corretor.",
      "Proposta enviada há 3 dias sem resposta: o agente retoma a conversa e tira dúvidas sobre rede.",
      "Aniversário de contrato chegando: o agente avisa a carteira e abre oportunidade de revisão.",
    ],
    faq: [
      { q: "O agente substitui o corretor?", a: "Não. Ele cuida do primeiro atendimento, da coleta e do acompanhamento; a consultoria e o fechamento continuam com o corretor." },
      { q: "Funciona com tráfego pago no Instagram e Facebook?", a: "Sim. Leads do Direct, de comentários e do WhatsApp entram no mesmo CRM e são atendidos pelo agente." },
      { q: "Como começo?", a: "Com uma conversa de diagnóstico e, se fizer sentido, uma POC de 7 dias configurada para a sua corretora." },
    ],
    related: [
      { label: "Blog: como vender plano de saúde pelo WhatsApp", to: "/blog/como-vender-plano-de-saude-pelo-whatsapp" },
      { label: "CRM para corretora de seguros", to: "/crm-para-corretora-de-seguros" },
    ],
  },
  {
    segment: "Seguro de Vida",
    slug: "agente-ia-seguro-de-vida",
    metaTitle: "Agente de IA para Seguro de Vida: mais conversas, mais apólices",
    metaDescription:
      "Agente de IA para vender seguro de vida no WhatsApp e Instagram: qualifica perfil, explica coberturas, agenda com o corretor e faz follow-up com CRM. POC de 7 dias.",
    heroTitle: "Agente de IA para Seguro de Vida: a conversa certa, no momento certo",
    heroSubtitle:
      "Seguro de vida raramente é comprado por impulso. Os agentes de IA nutrem o interesse, respondem dúvidas e levam o cliente pronto para a conversa com o corretor.",
    intro:
      "Seguro de vida exige confiança e várias conversas até a decisão. Os agentes de IA da OP2P iniciam o contato, entendem a fase de vida do lead (filhos, financiamento, empresa), explicam de forma simples o que é cobertura por morte, invalidez e doenças graves, e agendam a reunião com o corretor. Depois acompanham a proposta e lembram a carteira dos momentos de revisão. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como um agente de IA ajuda a vender seguro de vida?",
        a: "Ele qualifica o lead com perguntas sobre momento de vida e renda, responde dúvidas frequentes sobre coberturas, agenda a conversa com o corretor e faz follow-up até a assinatura, registrando tudo no CRM.",
      },
      {
        q: "A IA calcula o valor do seguro de vida?",
        a: "Não. O cálculo e a recomendação de capital segurado continuam com o corretor e a seguradora. O agente prepara a conversa com os dados que o corretor precisa.",
      },
    ],
    benefits: [
      { title: "Qualificação por momento de vida", desc: "Filhos, financiamento, sociedade: o agente entende a necessidade antes da reunião." },
      { title: "Dúvidas respondidas 24h", desc: "Coberturas, carência e beneficiários explicados com a linguagem da sua corretora." },
      { title: "Agenda com o corretor", desc: "O lead já sai da conversa com reunião marcada." },
      { title: "Nutrição sem esquecer", desc: "Follow-up na cadência certa para uma decisão que leva semanas." },
      { title: "Cross-sell da carteira", desc: "Clientes de auto e residencial recebem a oferta de vida no momento certo." },
      { title: "Histórico no CRM", desc: "Cada conversa fica registrada para o corretor retomar de onde parou." },
    ],
    useCases: [
      "Lead que acabou de ter filho pergunta sobre seguro de vida no Instagram: o agente qualifica e agenda.",
      "Cliente de seguro auto recebe convite para revisar a proteção da família.",
      "Empresário pergunta sobre seguro para sócios: o agente coleta dados e passa ao corretor.",
    ],
    faq: [
      { q: "Serve para seguro de vida em grupo?", a: "Sim. O agente identifica leads empresariais e coleta número de vidas e dados da empresa." },
      { q: "Preciso de site?", a: "Não. O agente atende onde o cliente já está: WhatsApp e Instagram." },
    ],
    related: [
      { label: "Blog: como vender seguro de vida pelo WhatsApp", to: "/blog/como-vender-seguro-de-vida-pelo-whatsapp" },
      { label: "Agente de IA para corretora de seguros", to: "/agente-ia-seguros" },
    ],
  },
  {
    segment: "Seguro Auto",
    slug: "agente-ia-seguro-auto",
    metaTitle: "Agente de IA para Seguro Auto: cotação completa em minutos",
    metaDescription:
      "Agente de IA para seguro auto: coleta placa, CEP, perfil do condutor e uso do veículo no WhatsApp e Instagram, entrega a cotação pronta e cuida da renovação.",
    heroTitle: "Agente de IA para Seguro Auto: do pedido de cotação à renovação",
    heroSubtitle:
      "Colete todos os dados do questionário de perfil sem ida e volta, responda na hora e nunca mais perca uma renovação por esquecimento.",
    intro:
      "Cotação de seguro auto depende de muitos dados: veículo, CEP de pernoite, condutor principal, idade, uso e garagem. Os agentes de IA da OP2P fazem essas perguntas no WhatsApp e no Instagram, conferem as respostas e entregam o questionário completo para o corretor rodar o cálculo. Depois acompanham a proposta e avisam o vencimento da apólice com antecedência. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Quais dados o agente coleta para cotar seguro auto?",
        a: "Modelo e ano do veículo ou placa, CEP de pernoite, dados do condutor principal, uso (particular, aplicativo, comercial), garagem e informações da apólice atual, quando houver.",
      },
      {
        q: "O agente cuida da renovação?",
        a: "Sim. Ele avisa o segurado antes do vencimento, confirma se houve mudança de perfil e entrega os dados atualizados para o corretor renovar.",
      },
    ],
    benefits: [
      { title: "Questionário completo", desc: "Todos os dados do perfil coletados em uma conversa." },
      { title: "Renovação em dia", desc: "Avisos antes do vencimento, sem planilha." },
      { title: "Lead do anúncio atendido", desc: "Comentários e Directs do Instagram viram cotação." },
      { title: "Orientação de sinistro", desc: "Primeiros passos explicados e corretor acionado na hora." },
      { title: "Cross-sell", desc: "Residencial e vida oferecidos para quem já é cliente de auto." },
      { title: "Kanban de cotações", desc: "Cada cotação com status, valor e próxima ação." },
    ],
    useCases: [
      "Motorista de aplicativo pede cotação às 22h e entrega todos os dados sem esperar o corretor.",
      "Apólice vence em 30 dias: o agente inicia a renovação.",
      "Cliente bate o carro e recebe orientação imediata sobre o que fazer.",
    ],
    faq: [
      { q: "Integra com multicálculo?", a: "O agente organiza os dados no formato que o corretor usa no multicálculo. Integrações específicas são avaliadas na implantação." },
      { q: "Funciona para frotas?", a: "Sim. O agente identifica pedidos de frota e coleta a lista de veículos." },
    ],
    related: [
      { label: "Blog: renovação de seguro sem perder clientes", to: "/blog/renovacao-de-seguros-automatica-whatsapp" },
      { label: "Agente de IA para corretora de seguros", to: "/agente-ia-seguros" },
    ],
  },
  {
    segment: "Seguradora",
    slug: "agente-ia-seguradora",
    metaTitle: "Agente de IA para Seguradora: atendimento 24h e corretores apoiados",
    metaDescription:
      "Agentes de IA para seguradoras e assessorias: atendimento a segurados e corretores no WhatsApp e Instagram, triagem de sinistro, segunda via e CRM integrado.",
    heroTitle: "Agentes de IA para Seguradoras e Assessorias",
    heroSubtitle:
      "Atenda segurados e corretores parceiros 24 horas, faça a triagem de sinistros e organize cada solicitação em um CRM, no WhatsApp e no Instagram.",
    intro:
      "Seguradoras, assessorias e plataformas de seguros recebem um volume alto de pedidos repetitivos: segunda via, status de proposta, aviso de sinistro, dúvidas de cobertura. Os agentes de IA da OP2P respondem esses pedidos na hora, separam por departamento (comercial, sinistro, financeiro, suporte ao corretor) e passam para a equipe humana o que precisa de análise. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como agentes de IA ajudam uma seguradora?",
        a: "Eles atendem segurados e corretores no WhatsApp e no Instagram, respondem dúvidas frequentes, fazem a triagem inicial de sinistros, encaminham cada caso ao departamento certo e registram todo o histórico no CRM.",
      },
      {
        q: "A IA decide sobre sinistro?",
        a: "Não. Ela coleta as informações iniciais e orienta os próximos passos. A análise e a decisão continuam com a equipe da seguradora.",
      },
    ],
    benefits: [
      { title: "Multiagentes por departamento", desc: "Comercial, sinistro, financeiro e suporte ao corretor." },
      { title: "Triagem de sinistro", desc: "Dados iniciais coletados e caso encaminhado na hora." },
      { title: "Apoio ao corretor parceiro", desc: "Status de proposta e materiais respondidos 24h." },
      { title: "Segunda via e dúvidas", desc: "Pedidos repetitivos resolvidos sem fila." },
      { title: "Histórico auditável", desc: "Cada interação registrada, com controle de acesso." },
      { title: "Integrações", desc: "Conexão com sistemas internos avaliada na implantação." },
    ],
    useCases: [
      "Segurado avisa sinistro no domingo e recebe a lista de documentos na hora.",
      "Corretor parceiro consulta o status de uma proposta pelo WhatsApp.",
      "Pedidos de segunda via saem da fila da central.",
    ],
    faq: [
      { q: "Atende assessorias de seguros?", a: "Sim. O modelo multiagentes atende assessorias que dão suporte a dezenas de corretores." },
      { q: "Como validamos?", a: "Com uma POC de 7 dias configurada para um fluxo da operação." },
    ],
    related: [{ label: "CRM para corretora de seguros", to: "/crm-para-corretora-de-seguros" }],
  },
  {
    segment: "CRM para Corretora de Seguros",
    slug: "crm-para-corretora-de-seguros",
    metaTitle: "CRM para Corretora de Seguros com WhatsApp, Instagram e IA | OP2P",
    metaDescription:
      "CRM para corretora de seguros entregue configurado: funil de cotações, renovações, carteira e Agentes de IA no WhatsApp e Instagram que alimentam o CRM sozinhos.",
    heroTitle: "CRM para Corretora de Seguros que se alimenta sozinho",
    heroSubtitle:
      "Cotações, propostas, renovações e carteira em um Kanban que se move com as conversas do WhatsApp e do Instagram, sem depender de alguém digitar.",
    intro:
      "A maioria das corretoras já tentou um CRM e voltou para a planilha, porque ninguém tem tempo de alimentar o sistema. No CRM da OP2P, os agentes de IA atendem os leads, registram cada conversa e movem a oportunidade no funil automaticamente: novo lead, cotação, proposta enviada, em negociação, apólice emitida, renovação. A OP2P entrega o CRM configurado para a corretora, com POC de 7 dias para validar. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Qual o melhor CRM para corretora de seguros?",
        a: "O melhor CRM é o que a equipe realmente usa. Para corretoras que vendem pelo WhatsApp e Instagram, um CRM com esses canais integrados e com Agentes de IA que registram as conversas evita o problema mais comum: o sistema desatualizado.",
      },
      {
        q: "O CRM da OP2P controla renovações?",
        a: "Sim. Cada apólice pode ter data de vencimento, e os agentes avisam o segurado com antecedência e abrem a oportunidade de renovação no funil.",
      },
      {
        q: "Quanto custa?",
        a: "O investimento é personalizado e apresentado na reunião de demonstração, com implantação assistida e POC de 7 dias.",
      },
    ],
    benefits: [
      { title: "Funil de seguros pronto", desc: "Etapas de cotação, proposta, emissão e renovação já configuradas." },
      { title: "WhatsApp e Instagram integrados", desc: "Todas as conversas no histórico de cada segurado." },
      { title: "Agentes de IA que alimentam o CRM", desc: "Lead atendido, dados registrados e card movido sem digitação." },
      { title: "Renovações controladas", desc: "Vencimentos com aviso e oportunidade aberta automaticamente." },
      { title: "Vários ramos", desc: "Auto, vida, saúde, residencial e empresarial no mesmo CRM." },
      { title: "Migração acompanhada", desc: "Importamos contatos e histórico da planilha ou do CRM anterior." },
    ],
    useCases: [
      "Corretora sai da planilha de renovações e passa a receber avisos automáticos.",
      "Leads de plano de saúde e de seguro auto entram em funis separados.",
      "Gestor acompanha cotações por corretor e por ramo no Kanban.",
    ],
    faq: [
      { q: "Preciso configurar sozinho?", a: "Não. A equipe da OP2P entrega o CRM configurado e acompanha a implantação." },
      { q: "Tem teste grátis?", a: "Oferecemos uma POC de 7 dias configurada para validar na sua operação." },
      { q: "Funciona para corretor autônomo?", a: "Sim, e também para corretoras com equipe e assessorias." },
    ],
    related: [
      { label: "Blog: melhor CRM para corretora de seguros", to: "/blog/melhor-crm-para-corretora-de-seguros" },
      { label: "Preços", to: "/precos" },
    ],
  },
  {
    segment: "Loja de Veículos e Concessionária",
    slug: "agente-ia-concessionaria-loja-de-veiculos",
    metaTitle: "Agente de IA para Concessionária e Loja de Veículos | CRM OP2P",
    metaDescription:
      "Agentes de IA para concessionárias, revendas e lojas de seminovos: atendem leads de Webmotors, Mobiauto, OLX, WhatsApp e Instagram em segundos e agendam test-drive no CRM.",
    heroTitle: "Agente de IA para Concessionária e Loja de Veículos",
    heroSubtitle:
      "Responda \"ainda está disponível?\" em segundos, qualifique troca e financiamento e leve o cliente ao test-drive — pelo WhatsApp, Instagram e portais, com o CRM se movendo sozinho.",
    intro:
      "Na venda de veículos, quem responde primeiro leva o cliente. Os leads chegam de Webmotors, Mobiauto, OLX, anúncios do Instagram e WhatsApp, e cada minuto de espera manda o comprador para a loja vizinha. Os agentes de IA da OP2P atendem na hora, confirmam o veículo no estoque, entendem se há carro na troca e interesse em financiamento, e agendam visita ou test-drive com o vendedor. Cada conversa vira um card no CRM, sem ninguém digitar. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "O que faz um agente de IA para concessionária?",
        a: "Ele atende os leads dos portais e das redes sociais em segundos, consulta o estoque integrado, responde dúvidas sobre o veículo, coleta dados do carro na troca e do interesse em financiamento, e agenda visita ou test-drive com o vendedor, registrando tudo no CRM.",
      },
      {
        q: "Funciona com Webmotors, Mobiauto e OLX?",
        a: "Sim. A OP2P tem integrações com Webmotors, Mobiauto e OLX, para que os leads desses portais entrem no CRM e sejam atendidos pelos agentes de IA junto com WhatsApp e Instagram.",
      },
      {
        q: "O agente fecha a venda sozinho?",
        a: "Não. Ele qualifica e aquece o cliente e entrega a negociação ao vendedor no momento certo, com o histórico completo da conversa.",
      },
    ],
    benefits: [
      { title: "Resposta em segundos", desc: "O lead do portal ou do anúncio é atendido antes de procurar outra loja." },
      { title: "Estoque integrado", desc: "Confirma disponibilidade, versão, ano e quilometragem do veículo anunciado." },
      { title: "Troca e financiamento", desc: "Coleta dados do usado e do interesse em parcelas antes de passar ao vendedor." },
      { title: "Test-drive agendado", desc: "Visita marcada na agenda do vendedor, com lembrete automático." },
      { title: "Follow-up de proposta", desc: "Cliente que sumiu é retomado na cadência certa, sem esquecimento." },
      { title: "Funil por vendedor", desc: "Gestor vê leads, visitas e negociações de cada vendedor no Kanban." },
    ],
    useCases: [
      "Lead da Webmotors pergunta às 23h se o carro ainda está disponível — o agente responde e agenda a visita.",
      "Cliente do Instagram quer saber se a loja aceita o usado dele na troca — o agente coleta modelo, ano e quilometragem.",
      "Proposta enviada no sábado é retomada automaticamente na segunda-feira.",
      "Gestor acompanha quantos test-drives cada vendedor marcou na semana.",
    ],
    faq: [
      { q: "Serve para loja de seminovos e multimarcas?", a: "Sim. Atende concessionárias, revendas multimarcas, lojas de seminovos e motos." },
      { q: "O agente informa preço e condições?", a: "Sim, conforme as regras definidas pela loja na implantação." },
      { q: "Como validamos?", a: "Com uma POC de 7 dias configurada para um fluxo da operação." },
    ],
    related: [
      { label: "Estoque Webmotors com IA", to: "/estoque-veiculos-ia-webmotors" },
      { label: "Por que POC e setup", to: "/por-que-poc-e-setup" },
    ],
  },
  {
    segment: "Loteadora e Incorporadora",
    slug: "agente-ia-loteadora-incorporadora",
    metaTitle: "Agentes de IA para Loteadoras, Incorporadoras e Construtoras | OP2P",
    metaDescription:
      "Agentes de IA para loteadoras, incorporadoras e construtoras: triagem de leads de lançamento no WhatsApp e Instagram, perfil de entrada e FGTS, e visita agendada com o corretor.",
    heroTitle: "Agentes de IA para Loteadoras e Incorporadoras",
    heroSubtitle:
      "Em fim de semana de lançamento, centenas de contatos chegam ao mesmo tempo. Os agentes de IA atendem todos, separam quem está pronto para comprar e agendam a visita ao stand.",
    intro:
      "Lançamento de loteamento ou empreendimento na planta gera picos de mensagens que nenhuma equipe comercial consegue responder a tempo. Os agentes de IA da OP2P atendem cada lead no WhatsApp e no Instagram, apresentam o empreendimento, entendem a capacidade de entrada, uso de FGTS e objetivo de compra, e agendam a visita com o corretor certo. O CRM organiza o funil por empreendimento, sem planilha. Tudo movido por inteligência artificial superinteligência — SI.",
    answers: [
      {
        q: "Como agentes de IA ajudam uma loteadora?",
        a: "Eles atendem todos os leads do lançamento ao mesmo tempo, explicam lotes, metragens e condições definidas pela loteadora, qualificam o perfil de entrada e agendam visitas ao stand ou ao loteamento, registrando tudo no CRM.",
      },
      {
        q: "O agente faz análise de crédito?",
        a: "Não. Ele faz uma triagem inicial de perfil — entrada disponível, uso de FGTS, renda declarada e prazo de compra — para o corretor priorizar os compradores mais prontos.",
      },
      {
        q: "Serve para incorporadora e construtora também?",
        a: "Sim. O mesmo fluxo atende lançamentos na planta, imóveis prontos e loteamentos, com funis separados por empreendimento.",
      },
    ],
    benefits: [
      { title: "Pico de lançamento sob controle", desc: "Centenas de conversas atendidas ao mesmo tempo, sem fila." },
      { title: "Triagem de perfil", desc: "Entrada, FGTS, renda e prazo coletados antes do corretor." },
      { title: "Visita ao stand agendada", desc: "Horário marcado com o corretor, com lembrete automático." },
      { title: "Funil por empreendimento", desc: "Cada lançamento com suas etapas e métricas no Kanban." },
      { title: "Distribuição entre corretores", desc: "Leads qualificados encaminhados para a equipe certa." },
      { title: "Contrato digital", desc: "Assinatura integrada com ZapSign, Clicksign e outras plataformas." },
    ],
    useCases: [
      "Anúncio de lançamento no Instagram gera 300 mensagens no sábado — todas atendidas na hora.",
      "Lead informa entrada e uso de FGTS e já recebe horário de visita ao loteamento.",
      "Curiosos ficam em nutrição automática; compradores prontos vão direto ao corretor.",
      "Diretor comercial acompanha visitas e propostas por empreendimento.",
    ],
    faq: [
      { q: "Integra com portais imobiliários?", a: "Sim. Há integrações com ZAP Imóveis, VivaReal e OLX." },
      { q: "O agente fala dos valores dos lotes?", a: "Sim, conforme as tabelas e regras definidas pela empresa na implantação." },
      { q: "Como validamos?", a: "Com uma POC de 7 dias configurada para um fluxo da operação." },
    ],
    related: [
      { label: "Agente de IA para imobiliária", to: "/agente-ia-imobiliaria" },
      { label: "Por que POC e setup", to: "/por-que-poc-e-setup" },
    ],
  },
];

export const SEGMENT_BY_SLUG = Object.fromEntries(SEGMENTS.map((s) => [s.slug, s]));
